/**
 * custom-facets.js
 *
 * Defines the <custom-filter-active> web component which renders active filter
 * tag pills for metafield-driven collection filters.
 *
 * Why a custom element?
 * ---------------------
 * facets.js re-renders sections via AJAX on every filter change. When the
 * section HTML is replaced, connectedCallback fires automatically on the new
 * element, so the pills update without any extra event wiring.
 *
 * The element reads its config from data-config (JSON array of filter objects
 * matching the custom.filter_config metafield schema) and reads active values
 * directly from the URL via URLSearchParams.
 *
 * Active tag links use is="facet-remove" so facets.js intercepts clicks and
 * performs AJAX section updates (same as native filter remove links).
 *
 * Product Type ordering
 * ---------------------
 * Product Type is always the first active tag(s):
 *   - If data-native-pt-label is present, Product Type is served from the
 *     native Search & Discovery filter (filter.p.product_type) and is
 *     handled here before the metafield config loop.
 *   - If product_type is in the metafield config (no data-native-pt-label),
 *     the config sort puts it first naturally.
 */

if (!customElements.get('custom-filter-active')) {
  class CustomFilterActive extends HTMLElement {
    connectedCallback() {
      this.render();
    }

    render() {
      const config = this._parseConfig();
      const params = new URLSearchParams(window.location.search);
      const collectionUrl = this.dataset.collectionUrl || window.location.pathname;
      const nativePtLabel = this.dataset.nativePtLabel || '';

      const tags = [];

      // ── Product Type: native S&D path (filter.p.product_type) ──────────────
      // Only used when the PIM hasn't included product_type in filter_config.
      // data-native-pt-label is set by custom-facets-active.liquid in this case.
      if (nativePtLabel) {
        const ptValues = params.getAll('filter.p.product_type');
        for (const value of ptValues) {
          tags.push({
            label: this._escape(value),
            removeUrl: this._buildRemoveUrl(params, 'filter.p.product_type', value),
          });
        }
      }

      // ── Custom metafield filters ────────────────────────────────────────────
      // product_type from config (if present) naturally sorts first; all others follow.
      const sorted = [
        ...config.filter((f) => f.key === 'product_type'),
        ...config.filter((f) => f.key !== 'product_type'),
      ];

      for (const filter of sorted) {
        const paramName = `filter.p.m.custom.${filter.key}`;

        if (filter.type === 'range') {
          const gteParam = `${paramName}.gte`;
          const lteParam = `${paramName}.lte`;
          const gteVal = params.get(gteParam);
          const lteVal = params.get(lteParam);

          if (gteVal !== null || lteVal !== null) {
            const from = gteVal !== null ? gteVal : '';
            const to = lteVal !== null ? lteVal : '';
            const label = `${this._escape(filter.label)}: ${this._escape(from)}–${this._escape(to)}`;
            tags.push({ label, removeUrl: this._buildRemoveRangeUrl(params, gteParam, lteParam) });
          }
        } else {
          const values = params.getAll(paramName);
          for (const value of values) {
            tags.push({
              label: this._escape(value),
              removeUrl: this._buildRemoveUrl(params, paramName, value),
            });
          }
        }
      }

      // ── Build HTML ──────────────────────────────────────────────────────────
      let html = '';
      for (const tag of tags) {
        html += `
          <a
            class="facet-remove btn btn--secondary flex relative"
            href="${tag.removeUrl}"
            is="facet-remove"
          >
            <div class="btn__text flex items-center justify-between gap-2">
              <div class="facet-title text-wrap flex items-center gap-2">${tag.label}</div>
              ${this._closeIcon()}
            </div>
          </a>`;
      }

      if (tags.length > 1) {
        html += `
          <a href="${collectionUrl}" class="facet-clear-all btn--link" is="facet-remove">
            ${window.theme?.strings?.clearAll || 'Clear all'}
          </a>`;
      }

      this.innerHTML = html;

      // ── Update all <facet-count> elements (drawer button + apply button) ───
      this._updateFacetCounts(tags.length);
    }

    // ─── URL helpers ──────────────────────────────────────────────────────────

    _buildRemoveUrl(params, paramName, valueToRemove) {
      const next = new URLSearchParams(params);
      const remaining = next.getAll(paramName).filter((v) => v !== valueToRemove);
      next.delete(paramName);
      remaining.forEach((v) => next.append(paramName, v));
      next.delete('page');
      return this._toUrl(next);
    }

    _buildRemoveRangeUrl(params, gteParam, lteParam) {
      const next = new URLSearchParams(params);
      next.delete(gteParam);
      next.delete(lteParam);
      next.delete('page');
      return this._toUrl(next);
    }

    _toUrl(params) {
      const qs = params.toString();
      return qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
    }

    // ─── UI helpers ───────────────────────────────────────────────────────────

    _updateFacetCounts(count) {
      document.querySelectorAll('facet-count').forEach((el) => {
        if (count === 0) {
          el.setAttribute('hidden', '');
          el.textContent = '(0)';
        } else {
          el.removeAttribute('hidden');
          el.textContent = `(${count})`;
        }
      });
    }

    _closeIcon() {
      // Matches the icon-close extra-small output from the theme's snippet
      return `<svg
        xmlns="http://www.w3.org/2000/svg"
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2.5"
        stroke-linecap="round"
        stroke-linejoin="round"
        aria-hidden="true"
        focusable="false"
      >
        <line x1="18" y1="6" x2="6" y2="18"></line>
        <line x1="6" y1="6" x2="18" y2="18"></line>
      </svg>`;
    }

    _parseConfig() {
      try {
        return JSON.parse(this.dataset.config || '[]');
      } catch {
        return [];
      }
    }

    _escape(str) {
      return String(str ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }
  }

  customElements.define('custom-filter-active', CustomFilterActive);
}

/**
 * syncFilterState
 *
 * Reads the current URL params and, for every custom filter accordion:
 *   1. Restores checkbox / range-input checked/value state.
 *   2. Keeps the accordion open when it has active selections.
 *   3. Updates the ".facet-active-count" badge in the summary.
 *
 * Called on DOMContentLoaded (for initial page loads with filters in the URL)
 * and on collection:rerendered (after every AJAX section update).
 *
 * Background: facets.js replaces #FacetFiltersContainer innerHTML via
 * history.replaceState + renderFacetFiltersContainer. The server-rendered
 * HTML relies on request.parameters, which is unreliable in Shopify's section
 * rendering context for filter.p.m.custom.* params. Reading from URLSearchParams
 * after the URL has been updated is the reliable source of truth.
 */
function syncFilterState() {
  const params = new URLSearchParams(window.location.search);

  document.querySelectorAll('form[is="facet-form"] [data-filter]').forEach(function (filterEl) {
    if (filterEl.tagName !== 'DETAILS') return;

    let activeCount = 0;

    // ── Checkboxes (list / boolean / native product type) ──────────────────
    filterEl.querySelectorAll('input[type="checkbox"]').forEach(function (input) {
      const name = input.getAttribute('name');
      if (!name) return;
      if (name.startsWith('filter.p.m.custom.') || name === 'filter.p.product_type') {
        const isChecked = params.getAll(name).includes(input.value);
        input.checked = isChecked;
        if (isChecked) activeCount++;
      }
    });

    // ── Range number inputs ─────────────────────────────────────────────────
    let rangeActive = false;
    filterEl.querySelectorAll('input[type="number"]').forEach(function (input) {
      const name = input.getAttribute('name');
      if (!name) return;
      if (name.startsWith('filter.p.m.custom.') && (name.endsWith('.gte') || name.endsWith('.lte'))) {
        const val = params.get(name);
        input.value = val !== null ? val : '';
        if (val !== null && val !== '') rangeActive = true;
      }
    });
    if (rangeActive) activeCount += 1;

    // ── Keep accordion open when filters are active ─────────────────────────
    if (activeCount > 0) {
      filterEl.open = true;
    }

    // ── Update the count badge ──────────────────────────────────────────────
    const badge = filterEl.querySelector('.facet-active-count');
    if (badge) {
      if (activeCount > 0) {
        badge.textContent = '(' + activeCount + ')';
        badge.hidden = false;
      } else {
        badge.hidden = true;
        badge.textContent = '';
      }
    }
  });
}

document.addEventListener('DOMContentLoaded', syncFilterState);
document.addEventListener('collection:rerendered', syncFilterState);
